import React, { useState, useEffect, useMemo, useRef } from 'react';
import DashboardLayout from '../components/admin/DashboardLayout';
import RequireAdmin from '../components/admin/RequireAdmin';
import { getAllMentors, MentorDB } from '../mentorService';
import { UserService, User } from '../services/UserService';
import { Mail, Send, FileText, Users, Filter, Check, AlertCircle, Copy, Loader2, Edit, RefreshCw, ExternalLink, X, Search, Database } from 'lucide-react';

export interface Recipient {
    id: string | number;
    name: string;
    email: string;
    type: 'mentor' | 'user';
    university?: string;
    major?: string;
    angkatan?: string | number;
    whatsapp?: string | null;
    instagram?: string | null;
    category?: string;
}

interface EmailTemplate {
    id: string;
    name: string;
    subject: string;
    body: string;
}

const DEFAULT_TEMPLATES: EmailTemplate[] = [
    {
        id: 'welcome',
        name: 'Welcome Message',
        subject: 'Selamat Bergabung di Alumni Mentorship Project IKAHATA 🎉',
        body: `Halo {name}! 👋

Terima kasih sudah bersedia menjadi mentor untuk adik-adik kita. Data kamu sudah tercatat di database AMP IKAHATA:

- Universitas: {university}
- Jurusan: {major}
- Angkatan: {angkatan}
- Kategori: {category}

Kamu bisa cek data lengkap dan profil publik kamu di website:
https://alumnihangtuah2025.vercel.app/database

Jika ada data yang salah atau ingin update, silakan hubungi admin (Gheren atau Faisal).

Salam hangat,
IKAHATA`
    },
    {
        id: 'update',
        name: 'Update Data Request',
        subject: 'Update Data Mentor AMP IKAHATA',
        body: `Halo {name}!

Kami sedang merapikan database mentor AMP. Mohon kesediaannya untuk mengecek data kamu apakah masih sesuai:

- Universitas: {university}
- Jurusan: {major}
- Angkatan: {angkatan}

Jika ada perubahan, mohon kabari kami ya. Terima kasih!

Salam,
IKAHATA`
    },
    {
        id: 'reminder',
        name: 'Mentor Reminder',
        subject: 'Reminder Mentor AMP IKAHATA 📚',
        body: `Halo {name}! 👋

Sekadar pengingat bahwa kamu terdaftar sebagai mentor di program AMP IKAHATA.

Terima kasih banyak sudah menyempatkan waktu untuk membimbing adik-adik kita. Sukses selalu buat kamu! 💪

Salam,
IKAHATA`
    },
    {
        id: 'custom',
        name: 'Custom Message',
        subject: 'Pesan dari AMP IKAHATA',
        body: `Halo {name}!

[Tulis pesan kamu di sini]

Salam,
IKAHATA`
    }
];

export default function EmailPage() {
    const [targetDb, setTargetDb] = useState<'mentor' | 'user'>('mentor');
    const [mentors, setMentors] = useState<MentorDB[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('welcome');

    // Editable subject and body (applies to any template)
    const [editSubject, setEditSubject] = useState(DEFAULT_TEMPLATES[0].subject);
    const [editBody, setEditBody] = useState(DEFAULT_TEMPLATES[0].body);

    const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

    // Filters
    const [filterCategory, setFilterCategory] = useState<string>('All');
    const [filterAngkatan, setFilterAngkatan] = useState<string>('All');
    const [filterHasEmail, setFilterHasEmail] = useState(true);

    // Selected recipients for sending
    const [selectedRecipientIds, setSelectedRecipientIds] = useState<Set<string | number>>(new Set());

    // Preview
    const [previewRecipient, setPreviewRecipient] = useState<Recipient | null>(null);

    // Send progress
    const [sendProgress, setSendProgress] = useState<{ sent: number; total: number; errors: string[]; statusText?: string } | null>(null);
    const [processLogs, setProcessLogs] = useState<string[]>([]);

    // Using a ref to track sending state within the async loop reliably
    const sendingRef = useRef(false);

    const addLog = (msg: string) => {
        setProcessLogs(prev => [`[${new Date().toLocaleTimeString()}] ${msg}`, ...prev].slice(0, 30));
    };

    useEffect(() => {
        loadData();
    }, []);

    // When template changes, update editable fields
    const handleTemplateChange = (templateId: string) => {
        const template = DEFAULT_TEMPLATES.find(t => t.id === templateId);
        if (template) {
            setSelectedTemplateId(templateId);
            setEditSubject(template.subject);
            setEditBody(template.body);
        }
    };

    const loadData = async () => {
        setLoading(true);
        try {
            const [mentorsRes, usersData] = await Promise.all([
                getAllMentors(),
                UserService.getAllUsers({ limit: 5000, offset: 0 })
            ]);
            if (mentorsRes.data) setMentors(mentorsRes.data);
            if (usersData) setUsers(usersData);
        } catch (error) {
            console.error('Failed to load data', error);
            setMessage({ type: 'error', text: 'Gagal memuat data.' });
        } finally {
            setLoading(false);
        }
    };

    // Mappings
    const allRecipients: Recipient[] = useMemo(() => {
        if (targetDb === 'mentor') {
            return mentors.map(m => ({
                id: m.id,
                name: m.name,
                email: m.email || '',
                type: 'mentor',
                university: m.university,
                major: m.major,
                angkatan: m.angkatan,
                whatsapp: m.whatsapp,
                instagram: m.instagram,
                category: m.category
            }));
        } else {
            return users.map(u => ({
                id: u.id,
                name: u.full_name || u.name || '',
                email: u.email,
                type: 'user',
                university: u.target_university_1 || '',
                major: u.target_major_1 || '',
                angkatan: u.angkatan || '',
                whatsapp: u.phone || '',
                instagram: u.instagram || '',
                category: u.role || 'user'
            }));
        }
    }, [targetDb, mentors, users]);

    // Get unique values for filters
    const categories = ['All', ...new Set(allRecipients.map(m => m.category).filter(Boolean))];
    const angkatans = ['All', ...new Set(allRecipients.map(m => String(m.angkatan)).filter(a => a !== 'undefined'))].sort();

    // Filter recipients
    const filteredRecipients = allRecipients.filter(m => {
        if (filterCategory !== 'All' && m.category !== filterCategory) return false;
        if (filterAngkatan !== 'All' && String(m.angkatan) !== filterAngkatan) return false;
        if (filterHasEmail && !m.email) return false;
        if (searchQuery && !m.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
        return true;
    });

    // Replace placeholders in template
    const replacePlaceholders = (text: string, recipient: Recipient): string => {
        return text
            .replace(/{name}/g, recipient.name)
            .replace(/{university}/g, recipient.university || '')
            .replace(/{major}/g, recipient.major || '')
            .replace(/{angkatan}/g, String(recipient.angkatan || ''))
            .replace(/{whatsapp}/g, recipient.whatsapp || '')
            .replace(/{instagram}/g, recipient.instagram || '')
            .replace(/{email}/g, recipient.email || '')
            .replace(/{category}/g, recipient.category || '');
    };

    const copyEmailList = () => {
        const emails = filteredRecipients
            .filter(m => m.email)
            .map(m => m.email)
            .join(', ');
        navigator.clipboard.writeText(emails);
        setMessage({ type: 'success', text: `✅ ${filteredRecipients.filter(m => m.email).length} email disalin ke clipboard` });
        setTimeout(() => setMessage(null), 3000);
    };

    const copyEmailContent = (recipient: Recipient) => {
        const content = `To: ${recipient.email}\nSubject: ${replacePlaceholders(editSubject, recipient)}\n\n${replacePlaceholders(editBody, recipient)}`;
        navigator.clipboard.writeText(content);
        setMessage({ type: 'success', text: `✅ Email content copied for ${recipient.name}` });
        setTimeout(() => setMessage(null), 3000);
    };

    // Open Gmail compose (more reliable than mailto:)
    const openGmailCompose = (recipient: Recipient) => {
        const subject = encodeURIComponent(replacePlaceholders(editSubject, recipient));
        const body = encodeURIComponent(replacePlaceholders(editBody, recipient));
        const to = encodeURIComponent(recipient.email || '');
        window.open(`https://mail.google.com/mail/?view=cm&to=${to}&su=${subject}&body=${body}`, '_blank');
    };

    // Send email via API with timeout and retry
    const sendEmailDirect = async (recipient: Recipient, retries = 1): Promise<boolean> => {
        for (let i = 0; i <= retries; i++) {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 25000); // 25s timeout

            try {
                const response = await fetch('/api/send-email', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        to: recipient.email,
                        subject: replacePlaceholders(editSubject, recipient),
                        body: replacePlaceholders(editBody, recipient)
                    }),
                    signal: controller.signal
                });

                clearTimeout(timeoutId);

                let data: any = {};
                const contentType = response.headers.get('content-type');

                if (contentType && contentType.includes('application/json')) {
                    try {
                        data = await response.json();
                    } catch (e) {
                        console.error('Failed to parse JSON error response:', e);
                    }
                }

                if (!response.ok) {
                    const errorMsg = data.message || data.error || `Server error (${response.status})`;
                    if (response.status >= 500 && i < retries) {
                        console.warn(`Attempt ${i + 1} failed, retrying...`);
                        continue;
                    }
                    throw new Error(errorMsg);
                }

                return true;
            } catch (error: any) {
                clearTimeout(timeoutId);
                if (error.name === 'AbortError') {
                    if (i < retries) {
                        console.warn(`Attempt ${i + 1} timed out, retrying...`);
                        continue;
                    }
                    throw new Error('API Timeout (25 detik). Koneksi kurang stabil.');
                }
                if (i < retries) continue;
                throw error;
            }
        }
        return false;
    };

    // Send to single recipient
    const sendToRecipient = async (recipient: Recipient) => {
        if (!recipient.email) {
            setMessage({ type: 'error', text: `❌ ${recipient.name} tidak punya email` });
            return;
        }

        setSending(true);
        try {
            await sendEmailDirect(recipient);
            setMessage({ type: 'success', text: `✅ Email berhasil dikirim ke ${recipient.name}` });
        } catch (error: any) {
            // If API not configured, offer Gmail as alternative
            if (error.message?.includes('configured')) {
                setMessage({
                    type: 'info',
                    text: `⚠️ Email API belum dikonfigurasi. Hubungi Admin untuk menset GMAIL_USER atau RESEND_API_KEY.`
                });
            } else if (error.message?.includes('limit reached')) {
                setMessage({
                    type: 'error',
                    text: `🚫 Limit email harian tercapai (100/hari). Silakan coba lagi besok.`
                });
            } else {
                setMessage({ type: 'error', text: `❌ Gagal kirim ke ${recipient.name}: ${error.message}` });
            }
        }
        setSending(false);
        setTimeout(() => setMessage(null), 5000);
    };

    // Bulk send to selected recipients
    const sendBulkEmail = async () => {
        const selectedRecipients = filteredRecipients.filter(m => selectedRecipientIds.has(m.id) && m.email);

        if (selectedRecipients.length === 0) {
            setMessage({ type: 'error', text: '❌ Pilih minimal 1 penerima dengan email' });
            return;
        }

        if (!window.confirm(`Kirim email ke ${selectedRecipients.length} penerima?`)) return;

        setSending(true);
        sendingRef.current = true;
        setProcessLogs([]);
        addLog(`Memulai pengiriman ke ${selectedRecipients.length} penerima...`);
        setSendProgress({ sent: 0, total: selectedRecipients.length, errors: [], statusText: 'Inisialisasi...' });

        const errors: string[] = [];
        let sent = 0;
        let batchCount = 0;

        for (const recipient of selectedRecipients) {
            if (!sendingRef.current) {
                addLog('⛔ Pengiriman dihentikan oleh pengguna.');
                break;
            }

            // Cool-down pause: every 10 emails, wait 45 seconds
            if (batchCount > 0 && batchCount % 10 === 0) {
                const waitTime = 45;
                for (let i = waitTime; i > 0; i--) {
                    if (!sendingRef.current) break;
                    setSendProgress({
                        sent,
                        total: selectedRecipients.length,
                        errors,
                        statusText: `☕ Cool-down (Batch ${Math.floor(batchCount / 10)}): Lanjut dalam ${i} detik...`
                    });
                    await new Promise(resolve => setTimeout(resolve, 1000));
                }
                if (!sendingRef.current) break;
            }

            try {
                const remaining = selectedRecipients.length - sent;
                const estMinutes = Math.ceil(remaining * 9 / 60);

                addLog(`Mengirim ke ${recipient.name}...`);
                setSendProgress({
                    sent,
                    total: selectedRecipients.length,
                    errors,
                    statusText: `📤 Mengirim ke ${recipient.name}... (Est: ${estMinutes} min)`
                });

                const startTime = Date.now();
                await sendEmailDirect(recipient, 1);
                const duration = ((Date.now() - startTime) / 1000).toFixed(1);

                addLog(`✅ Berhasil kirim ke ${recipient.name} (${duration}s)`);
                sent++;
                batchCount++;
            } catch (error: any) {
                const errorMsg = error.message || 'Unknown error';
                addLog(`❌ Gagal ke ${recipient.name}: ${errorMsg}`);
                errors.push(`${recipient.name}: ${errorMsg}`);

                const lowerMsg = errorMsg.toLowerCase();
                if (lowerMsg.includes('configured')) {
                    setMessage({ type: 'info', text: `⚠️ Email API belum dikonfigurasi.` });
                    break;
                }

                if (lowerMsg.includes('limit reached')) {
                    setMessage({ type: 'error', text: `🚫 Limit email harian tercapai.` });
                    break;
                }
            }

            setSendProgress({ sent, total: selectedRecipients.length, errors });

            if (sent < selectedRecipients.length && sendingRef.current) {
                const delayTime = 6;
                for (let i = delayTime; i > 0; i--) {
                    if (!sendingRef.current) break;
                    setSendProgress({
                        sent,
                        total: selectedRecipients.length,
                        errors,
                        statusText: `⏱️ Jeda aman: ${i} detik...`
                    });
                    await new Promise(resolve => setTimeout(resolve, 1000));
                }
            }
        }

        setSending(false);
        sendingRef.current = false;
        addLog(`Selesai. Terkirim: ${sent}, Gagal: ${errors.length}`);

        // Final progress update to show "Completed" status
        setSendProgress({
            sent,
            total: selectedRecipients.length,
            errors,
            statusText: `✅ Pengiriman Selesai: ${sent}/${selectedRecipients.length} Terkirim`
        });

        if (errors.length === 0 && sent > 0) {
            setMessage({ type: 'success', text: `✅ Berhasil kirim ${sent} email!` });
        } else if (sent > 0 || errors.length > 0) {
            setMessage({ type: 'info', text: `ℹ️ Selesai. Terkirim: ${sent}, Gagal: ${errors.length}` });
        }

        setSelectedRecipientIds(new Set());
    };

    const stopSending = () => {
        sendingRef.current = false;
        setSending(false);
        addLog('Stopping process...');
    };

    const toggleSelectRecipient = (id: string | number) => {
        const newSet = new Set(selectedRecipientIds);
        if (newSet.has(id)) {
            newSet.delete(id);
        } else {
            newSet.add(id);
        }
        setSelectedRecipientIds(newSet);
    };

    const selectAllFiltered = () => {
        const emailRecipients = filteredRecipients.filter(m => m.email);
        if (selectedRecipientIds.size === emailRecipients.length) {
            setSelectedRecipientIds(new Set());
        } else {
            setSelectedRecipientIds(new Set(emailRecipients.map(m => m.id)));
        }
    };

    if (loading) {
        return (
            <RequireAdmin>
                <DashboardLayout>
                    <div className="p-6 flex items-center justify-center">
                        <Loader2 className="animate-spin" size={32} />
                    </div>
                </DashboardLayout>
            </RequireAdmin>
        );
    }

    return (
        <RequireAdmin>
            <DashboardLayout>
                <div className="p-6">
                    <h1 className="text-2xl font-bold flex items-center gap-2 mb-6">
                        <Mail size={28} /> Email Template & Send
                    </h1>

                    {message && (
                        <div className={`mb-4 p-4 rounded-xl flex items-center gap-3 ${message.type === 'success' ? 'bg-green-900/50 border border-green-500' :
                            message.type === 'error' ? 'bg-red-900/50 border border-red-500' :
                                'bg-blue-900/50 border border-blue-500'
                            }`}>
                            {message.type === 'success' && <Check size={20} />}
                            {message.type === 'error' && <AlertCircle size={20} />}
                            {message.type === 'info' && <AlertCircle size={20} />}
                            <span className="flex-1">{message.text}</span>
                        </div>
                    )}

                    {/* Send Progress */}
                    {sendProgress && (
                        <div className="mb-4 p-4 bg-indigo-900/50 border border-indigo-500 rounded-xl">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-3">
                                    {sending ? (
                                        <Loader2 className="animate-spin text-indigo-400" size={20} />
                                    ) : (
                                        <Check className="text-green-400" size={20} />
                                    )}
                                    <span className="font-semibold">{sendProgress.statusText || `Mengirim: ${sendProgress.sent}/${sendProgress.total}`}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="text-xs text-indigo-300">
                                        {Math.round((sendProgress.sent / sendProgress.total) * 100)}%
                                    </span>
                                    {!sending && (
                                        <button
                                            onClick={() => { setSendProgress(null); setProcessLogs([]); }}
                                            className="text-slate-400 hover:text-white p-1"
                                        >
                                            <X size={16} />
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="bg-slate-700 rounded-full h-2 mb-4">
                                <div
                                    className="bg-indigo-500 h-2 rounded-full transition-all duration-500"
                                    style={{ width: `${(sendProgress.sent / sendProgress.total) * 100}%` }}
                                />
                            </div>

                            {/* Real-time Error Log */}
                            {sendProgress.errors.length > 0 && (
                                <div className="mt-4 pt-4 border-t border-indigo-500/30">
                                    <div className="flex items-center gap-2 text-red-400 text-sm font-bold mb-2">
                                        <AlertCircle size={14} /> Gagal Terkirim ({sendProgress.errors.length}):
                                    </div>
                                    <div className="max-h-32 overflow-y-auto space-y-1 custom-scrollbar pr-2">
                                        {sendProgress.errors.map((err, idx) => (
                                            <div key={idx} className="text-xs bg-red-900/20 border border-red-900/30 p-2 rounded text-red-300 flex items-start gap-2 animate-fadeIn">
                                                <span className="opacity-50">•</span>
                                                <span>{err}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Detailed Log Section */}
                            {processLogs.length > 0 && (
                                <div className="mt-4 pt-4 border-t border-indigo-500/30">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="text-indigo-300 text-xs font-bold uppercase tracking-wider">Detailed Process Log</div>
                                        {sending && (
                                            <button
                                                onClick={stopSending}
                                                className="px-2 py-0.5 bg-red-600 hover:bg-red-700 rounded text-[10px] font-bold uppercase"
                                            >
                                                Stop
                                            </button>
                                        )}
                                    </div>
                                    <div className="bg-slate-900/50 rounded-lg p-2 max-h-40 overflow-y-auto font-mono text-[10px] space-y-1">
                                        {processLogs.map((log, idx) => (
                                            <div key={idx} className={`${log.includes('✅') ? 'text-green-400' :
                                                log.includes('❌') ? 'text-red-400' :
                                                    log.includes('⚠️') ? 'text-yellow-400' :
                                                        'text-slate-400'
                                                }`}>
                                                {log}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Left: Template Editor */}
                        <div className="space-y-4">
                            {/* Template Selection */}
                            <div className="bg-slate-800 rounded-xl p-4">
                                <label className="block text-sm text-slate-400 mb-2">Template (pilih untuk load)</label>
                                <div className="grid grid-cols-2 gap-2">
                                    {DEFAULT_TEMPLATES.map(t => (
                                        <button
                                            key={t.id}
                                            onClick={() => handleTemplateChange(t.id)}
                                            className={`p-3 rounded-lg text-left transition ${selectedTemplateId === t.id
                                                ? 'bg-indigo-600 text-white'
                                                : 'bg-slate-700 hover:bg-slate-600'
                                                }`}
                                        >
                                            <FileText size={18} className="mb-1" />
                                            <span className="text-sm">{t.name}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Editable Subject */}
                            <div className="bg-slate-800 rounded-xl p-4">
                                <label className="block text-sm text-slate-400 mb-2 flex items-center gap-2">
                                    <Edit size={14} /> Subject (bisa diedit)
                                </label>
                                <input
                                    type="text"
                                    value={editSubject}
                                    onChange={e => setEditSubject(e.target.value)}
                                    placeholder="Subject email..."
                                    className="w-full px-3 py-2 bg-slate-700 rounded border border-slate-600 focus:border-indigo-500 outline-none"
                                />
                            </div>

                            {/* Editable Body */}
                            <div className="bg-slate-800 rounded-xl p-4">
                                <label className="block text-sm text-slate-400 mb-2 flex items-center gap-2">
                                    <Edit size={14} /> Body (bisa diedit)
                                </label>
                                <textarea
                                    value={editBody}
                                    onChange={e => setEditBody(e.target.value)}
                                    placeholder="Isi email... Gunakan {name}, {university}, {major}, {angkatan} untuk placeholder"
                                    rows={12}
                                    className="w-full px-3 py-2 bg-slate-700 rounded border border-slate-600 focus:border-indigo-500 outline-none font-mono text-sm"
                                />
                                <p className="text-xs text-slate-500 mt-2">
                                    Placeholders: {'{name}'}, {'{university}'}, {'{major}'}, {'{angkatan}'}, {'{email}'}, {'{whatsapp}'}
                                </p>
                            </div>

                            {/* Reset Template Button */}
                            <button
                                onClick={() => handleTemplateChange(selectedTemplateId)}
                                className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm"
                            >
                                <RefreshCw size={16} /> Reset ke Template Asli
                            </button>
                        </div>

                        {/* Right: Recipients & Preview */}
                        <div className="space-y-4">
                            {/* Database Selection */}
                            <div className="bg-slate-800 rounded-xl p-4">
                                <h2 className="text-lg font-semibold flex items-center gap-2 mb-3">
                                    <Database size={20} /> Target Database
                                </h2>
                                <div className="flex bg-slate-700/50 p-1 rounded-lg">
                                    <button
                                        onClick={() => { setTargetDb('mentor'); setSelectedRecipientIds(new Set()); }}
                                        className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-semibold rounded-md transition-all ${targetDb === 'mentor' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:text-white hover:bg-slate-600/50'}`}
                                    >
                                        <Users size={16} /> Mentors
                                    </button>
                                    <button
                                        onClick={() => { setTargetDb('user'); setSelectedRecipientIds(new Set()); }}
                                        className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-semibold rounded-md transition-all ${targetDb === 'user' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-400 hover:text-white hover:bg-slate-600/50'}`}
                                    >
                                        <Users size={16} /> Users (SNBT)
                                    </button>
                                </div>
                            </div>

                            {/* Filters & Search */}
                            <div className="bg-slate-800 rounded-xl p-4">
                                <h2 className="text-lg font-semibold flex items-center gap-2 mb-3">
                                    <Filter size={20} /> Filter Penerima
                                </h2>

                                <div className="mb-3 relative">
                                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input
                                        type="text"
                                        placeholder="Cari nama penerima..."
                                        value={searchQuery}
                                        onChange={e => setSearchQuery(e.target.value)}
                                        className="w-full pl-9 pr-3 py-2 bg-slate-700 rounded border border-slate-600 focus:border-indigo-500 outline-none text-sm text-slate-200"
                                    />
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-sm text-slate-400 mb-1">{targetDb === 'mentor' ? 'Kategori' : 'Role'}</label>
                                        <select
                                            value={filterCategory}
                                            onChange={e => setFilterCategory(e.target.value)}
                                            className="w-full px-3 py-2 bg-slate-700 rounded border border-slate-600 text-sm"
                                        >
                                            {categories.map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm text-slate-400 mb-1">Angkatan</label>
                                        <select
                                            value={filterAngkatan}
                                            onChange={e => setFilterAngkatan(e.target.value)}
                                            className="w-full px-3 py-2 bg-slate-700 rounded border border-slate-600 text-sm"
                                        >
                                            {angkatans.map(a => <option key={a} value={a}>{a}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <label className="flex items-center gap-2 mt-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={filterHasEmail}
                                        onChange={e => setFilterHasEmail(e.target.checked)}
                                        className="w-4 h-4 cursor-pointer"
                                    />
                                    <span className="text-sm text-slate-400">Hanya yang punya email</span>
                                </label>
                            </div>

                            {/* Recipients List with Checkboxes */}
                            <div className="bg-slate-800 rounded-xl p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <h2 className="text-lg font-semibold flex items-center gap-2">
                                        <Users size={20} /> Penerima ({filteredRecipients.filter(m => m.email).length})
                                    </h2>
                                    <button
                                        onClick={selectAllFiltered}
                                        className="text-sm text-indigo-400 hover:text-indigo-300"
                                    >
                                        {selectedRecipientIds.size === filteredRecipients.filter(m => m.email).length ? 'Batal Pilih Semua' : 'Pilih Semua'}
                                    </button>
                                </div>

                                <div className="max-h-48 overflow-y-auto space-y-1 pr-2 custom-scrollbar">
                                    {filteredRecipients.filter(m => m.email).slice(0, 50).map(m => (
                                        <label
                                            key={m.id}
                                            className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-700/50 ${selectedRecipientIds.has(m.id) ? 'bg-indigo-900/30' : ''
                                                }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedRecipientIds.has(m.id)}
                                                onChange={() => toggleSelectRecipient(m.id)}
                                                className="w-4 h-4"
                                            />
                                            <span className="text-sm truncate flex-1">{m.name}</span>
                                            <span className="text-xs text-slate-500 truncate max-w-[120px]">{m.email}</span>
                                        </label>
                                    ))}
                                    {filteredRecipients.filter(m => m.email).length > 50 && (
                                        <p className="text-xs text-slate-500 p-2">+{filteredRecipients.filter(m => m.email).length - 50} lainnya...</p>
                                    )}
                                </div>

                                {selectedRecipientIds.size > 0 && (
                                    <div className="mt-3 p-2 bg-indigo-900/30 rounded-lg text-sm">
                                        <strong>{selectedRecipientIds.size}</strong> penerima dipilih
                                    </div>
                                )}
                            </div>

                            {/* Actions */}
                            <div className="bg-slate-800 rounded-xl p-4">
                                <h2 className="text-lg font-semibold mb-3">Kirim Email</h2>
                                <div className="space-y-2">
                                    <button
                                        onClick={sendBulkEmail}
                                        disabled={sending || selectedRecipientIds.size === 0}
                                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition font-semibold"
                                    >
                                        {sending ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
                                        Kirim ke {selectedRecipientIds.size} Penerima (Direct)
                                    </button>
                                    <p className="text-xs text-slate-500 text-center">
                                        Powered by Resend API. Daily limit: 100 emails.
                                    </p>
                                    <div className="border-t border-slate-700 my-3 pt-3">
                                        <p className="text-sm text-slate-400 mb-2">Alternatif (manual):</p>
                                        <button
                                            onClick={copyEmailList}
                                            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition text-sm"
                                        >
                                            <Copy size={16} />
                                            Copy Semua Alamat Email
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Preview */}
                            <div className="bg-slate-800 rounded-xl p-4">
                                <h2 className="text-lg font-semibold mb-3">Preview & Kirim Satuan</h2>
                                <select
                                    onChange={e => {
                                        const recipient = filteredRecipients.find(m => String(m.id) === e.target.value);
                                        setPreviewRecipient(recipient || null);
                                    }}
                                    className="w-full px-3 py-2 bg-slate-700 rounded border border-slate-600 mb-3 text-sm"
                                >
                                    <option value="">Pilih penerima untuk preview...</option>
                                    {filteredRecipients.slice(0, 30).map(m => (
                                        <option key={m.id} value={m.id}>{m.name} - {m.email || '(no email)'}</option>
                                    ))}
                                </select>

                                {previewRecipient && (
                                    <div className="bg-slate-900 rounded-lg p-4 space-y-3">
                                        <div>
                                            <span className="text-xs text-slate-500">To:</span>
                                            <p className="text-sm">{previewRecipient.email || '(no email)'}</p>
                                        </div>
                                        <div>
                                            <span className="text-xs text-slate-500">Subject:</span>
                                            <p className="text-sm font-semibold">
                                                {replacePlaceholders(editSubject, previewRecipient)}
                                            </p>
                                        </div>
                                        <div>
                                            <span className="text-xs text-slate-500">Body:</span>
                                            <pre className="text-sm whitespace-pre-wrap mt-1 text-slate-300 max-h-32 overflow-y-auto custom-scrollbar">
                                                {replacePlaceholders(editBody, previewRecipient)}
                                            </pre>
                                        </div>
                                        {previewRecipient.email && (
                                            <div className="flex gap-2 pt-2">
                                                <button
                                                    onClick={() => sendToRecipient(previewRecipient)}
                                                    disabled={sending}
                                                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 rounded-lg transition text-sm font-medium"
                                                >
                                                    <Send size={14} />
                                                    Kirim Direct
                                                </button>
                                                <button
                                                    onClick={() => openGmailCompose(previewRecipient)}
                                                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition text-sm font-medium"
                                                >
                                                    <ExternalLink size={14} />
                                                    Buka Gmail
                                                </button>
                                                <button
                                                    onClick={() => copyEmailContent(previewRecipient)}
                                                    className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition"
                                                    title="Copy content"
                                                >
                                                    <Copy size={14} />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </DashboardLayout>
        </RequireAdmin>
    );
}
